import { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { CalendarIcon, X, Loader2, SunIcon, Clock3Icon, MoonIcon, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Post, PostsApi, AIApi } from '@/services/api';
import { useCreatePost, useUpdatePost, usePosts } from '@/hooks/usePosts';
import { 
  findNextOptimalTimeSlot, 
  createTimeSlot,
  getOptimalPostingTimes
} from '@/utils/dateUtils';
import { PlatformSelector } from './PlatformSelector';
import { useSocialAccounts } from '@/hooks/useSocialAccounts';
import { useToast } from '@/hooks/useToast';

interface PostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post?: Post;
}

const PostModal: React.FC<PostModalProps> = ({ isOpen, onClose, post }) => {
  const [content, setContent] = useState(post ? post.content : '');
  const [platform, setPlatform] = useState(post ? post.platform : 'x');
  const [scheduledTime, setScheduledTime] = useState<Date>(new Date());
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(undefined);
  const [imageFilename, setImageFilename] = useState<string | null | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [aiPrompt, setAIPrompt] = useState("Do a web search for an AI advancement or bit of news that happened recently. Write a banger X post about it.");
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState<string | null>(null);
  const { toast } = useToast();
  const { isPlatformConnected, connectedAccounts } = useSocialAccounts();
  const [newlyUploadedImageDetails, setNewlyUploadedImageDetails] = useState<{ url: string; filename: string } | null>(null);

  // Fetch all posts to determine optimal scheduling
  const { data: allPosts } = usePosts();

  // Maximum file size: 16MB
  const MAX_FILE_SIZE = 16 * 1024 * 1024;

  // Get fresh optimal times
  const optimalTimes = getOptimalPostingTimes();

  // Set initial platform to the first connected account if not editing
  useEffect(() => {
    if (isOpen && !post && connectedAccounts.length > 0) {
      setPlatform(connectedAccounts[0].platform);
    }
  }, [isOpen, post, connectedAccounts]);

  // Find the next available optimal time slot when modal opens
  useEffect(() => {
    if (isOpen && !post) {
      if (allPosts && Array.isArray(allPosts)) {
        // Get scheduled times of all unposted posts
        const scheduledTimes = allPosts
          .filter((p: Post) => p.status !== 'posted')
          .map((p: Post) => p.scheduled_time);
        
        // Find the next available optimal time slot
        const nextSlot = findNextOptimalTimeSlot(scheduledTimes);
        setScheduledTime(nextSlot);
      } else {
        // Default to next optimal time if posts aren't loaded yet
        const now = new Date();
        const freshOptimalTimes = getOptimalPostingTimes(); // Get fresh times
        const optimalTimes = freshOptimalTimes.map((slot: { hour: number; minute: number }) => 
          createTimeSlot(now, slot.hour, slot.minute)
        );
        
        // Find the first time slot in the future
        const futureSlot = optimalTimes.find((time: Date) => time > now);
        if (futureSlot) {
          setScheduledTime(futureSlot);
        } else {
          // If all today's slots are past, use tomorrow morning
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);
          setScheduledTime(createTimeSlot(
            tomorrow, 
            freshOptimalTimes[0].hour, 
            freshOptimalTimes[0].minute
          ));
        }
      }
    }
  }, [isOpen, post, allPosts]);

  // Reset form state based on modal open/close state and whether editing vs creating
  useEffect(() => {
    // Helper to revoke preview URL
    const revokePreviewUrl = () => {
      setImageObjectUrl(currentUrl => {
        if (currentUrl) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };

    if (isOpen) {
      // --- Reset non-content fields that depend on post/accounts ---
      const initialPlatform = post?.platform || (connectedAccounts.length > 0 ? connectedAccounts[0].platform : 'x');
      setPlatform(initialPlatform);
      
      // Only set scheduled time here when editing an existing post to avoid overwriting
      // the user's manual selection for new posts.
      if (post) {
        setScheduledTime(new Date(post.scheduled_time));
      }
      
      setImageUrl(post?.image_url || null); // Use null instead of undefined for consistency?
      setImageFilename(post?.image_filename || null);
            
      // --- Reset other state --- 
      setIsGenerating(false);
      setUploadError(null);
      setAIPrompt("Do a web search for an AI advancement or bit of news that happened recently. Write a banger X post about it.");
      setShowPromptEditor(false);
      setNewlyUploadedImageDetails(null);

    } else {
      // Modal is closed - clear everything including preview
      setContent(''); // Clear content when closing
      setPlatform(connectedAccounts.length > 0 ? connectedAccounts[0].platform : 'x');
      setScheduledTime(new Date()); // Reset time
      setImageUrl(null);
      setImageFilename(null);
      setIsGenerating(false);
      setUploadError(null);
      setAIPrompt("Do a web search for an AI advancement or bit of news that happened recently. Write a banger X post about it.");
      setShowPromptEditor(false);
      revokePreviewUrl(); // Keep clear on close
      setNewlyUploadedImageDetails(null);
    }

    // Dependencies for resetting fields other than content
  }, [isOpen, post?.id, connectedAccounts]); // Depend on post.id for stability but not on allPosts to avoid reseting user selection

  // --- Separate Effect for Content Reset --- 
  // Reset content ONLY when the post being edited changes (or goes from edit to new)
  useEffect(() => {
    if (isOpen) {
      setContent(post ? post.content : '');
    }
    // This effect ONLY depends on isOpen and the post object identity
    // It won't re-run just because connectedAccounts changes.
  }, [isOpen, post]);
  // --- End Content Reset Effect ---

  // --- Separate Effect for Image URL/Filename Reset ---
  useEffect(() => {
    if (isOpen) {
      // Set initial image details based *only* on the post prop
      setImageUrl(post?.image_url || null);
      setImageFilename(post?.image_filename || null);
      setNewlyUploadedImageDetails(null);
    } 
    // else: Don't clear here, cleared on modal close in main effect or by remove/upload handlers
  }, [isOpen, post]);
  // --- End Image Reset Effect ---

  // --- Effect to fetch image blob ONLY when editing an existing post --- 
  useEffect(() => {
    let objectUrlToRevoke: string | null = null; 
    setImageLoadError(null);
    setImageObjectUrl(null); // Clear any previous preview

    // Only run if we are editing a post AND that post has an image URL
    if (post && post.image_url) { 
      setIsImageLoading(true);

      const fetchImage = async () => {
        try {
          // Get filename from the stored post data
          const filename = post.image_filename; 
          if (!filename) {
            throw new Error("Post data is missing image filename");
          }
          
          // Call the API method which uses fetchWithRetry
          const blob = await PostsApi.getImageBlob(filename);
          
          objectUrlToRevoke = URL.createObjectURL(blob);
          setImageObjectUrl(objectUrlToRevoke);
          setImageLoadError(null); 

        } catch (error) {
          console.error('Failed to load existing image:', error);
          setImageLoadError(error instanceof Error ? error.message : 'An unknown error occurred');
          setImageObjectUrl(null); 
        } finally {
          setIsImageLoading(false);
        }
      };

      fetchImage();
    } else {
      // Not editing or post has no image, ensure loading is false
      setIsImageLoading(false); 
    }

    // Cleanup
    return () => {
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  // Depend only on the post object (specifically its url/filename)
  }, [post]); 
  // --- End fetch image effect ---

  const createPost = useCreatePost();
  const updatePost = useUpdatePost();

  // Handle selecting a time slot
  const handleTimeSlotSelect = (slot: { hour: number; minute: number }) => {
    const date = new Date();
    if (date.getHours() > slot.hour || (date.getHours() === slot.hour && date.getMinutes() >= slot.minute)) {
      // If the slot is already past for today, use tomorrow
      date.setDate(date.getDate() + 1);
    }
    
    setScheduledTime(createTimeSlot(date, slot.hour, slot.minute));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setUploadError('');
    // --- Clear previous previews --- 
    setImageUrl(undefined);
    setImageFilename(undefined);
    if (imageObjectUrl) {
       URL.revokeObjectURL(imageObjectUrl); // Revoke previous temporary URL
    }
    setImageObjectUrl(null);
    // --- End clear previous --- 
    
    if (files && files.length > 0) {
      const file = files[0];
      
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`);
        event.target.value = ''; 
        return;
      }
      
      // --- Create temporary preview URL immediately --- 
      const tempPreviewUrl = URL.createObjectURL(file);
      setImageObjectUrl(tempPreviewUrl); // Show preview right away
      // --- End temporary preview --- 

      setIsGenerating(true); // Indicate API upload is in progress
      try {
        const response = await PostsApi.uploadImage(file);
        
        // Store URL and filename from API response for saving the post later
        setImageUrl(response.url); 
        setImageFilename(response.filename);
        setNewlyUploadedImageDetails({ url: response.url, filename: response.filename });
        setUploadError('');
      } catch (error) {
        setUploadError('Failed to upload image to server');
        console.error('Image upload API error:', error);
        // Keep the temporary preview or clear it? Let's keep it for now.
      } finally {
        setIsGenerating(false); // API upload finished (success or fail)
      }
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(undefined);
    setImageFilename(undefined);
    // --- Revoke object URL if it exists --- 
    if (imageObjectUrl) {
      URL.revokeObjectURL(imageObjectUrl);
    }
    setImageObjectUrl(null); 
    setNewlyUploadedImageDetails(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGetMeStarted = async () => {
    setIsGenerating(true);
    try {
      const text = await AIApi.generateContent(aiPrompt);
      setContent(text);
    } catch (error) {
      console.error('Failed to generate AI content', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isGenerating) {
      toast({
        title: "Wait for upload",
        description: "Please wait for image upload to complete before saving",
        type: "warning"
      });
      return;
    }

    // Check if the selected platform is connected
    if (!isPlatformConnected(platform)) {
      toast({
        title: "Platform not connected",
        description: `You need to connect your ${platform === 'x' ? 'X (Twitter)' : platform} account before posting. Go to Profile → Connected Accounts.`,
        type: "error"
      });
      return;
    }
    
    try {
      const payload = {
        content,
        scheduled_time: scheduledTime.toISOString(),
        platform: platform,
        status: 'scheduled' as const,
        image_url: newlyUploadedImageDetails ? newlyUploadedImageDetails.url : (imageUrl || null),
        image_filename: newlyUploadedImageDetails ? newlyUploadedImageDetails.filename : (imageFilename || null)
      };

      if (post) {
        await updatePost.mutateAsync({ 
          id: post.id, 
          post: payload 
        });
      } else {
        await createPost.mutateAsync(payload);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save post:', error);
      toast({
        title: "Save Failed",
        description: error instanceof Error ? error.message : "Failed to save post",
        type: "error"
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-card">
        <DialogHeader>
          <DialogTitle className="text-foreground">{post ? 'Edit Post' : 'Create Post'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Platform selector */}
          <PlatformSelector 
            selectedPlatform={platform} 
            onChange={setPlatform} 
          />

          {/* Show warning if no connected accounts */}
          {connectedAccounts.length === 0 && (
            <div className="flex items-center p-3 gap-2 bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 rounded-md border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <p className="text-sm">
                You don't have any connected social media accounts. 
                Please <a href="/profile" className="underline">connect an account</a> to post.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="content">Content</Label>
              <Button 
                type="button"
                onClick={handleGetMeStarted}
                disabled={isGenerating}
                variant="outline"
                size="sm"
              >
                {isGenerating ? 'Generating...' : 'Get me started'}
              </Button>
              <Button 
                type="button"
                onClick={() => setShowPromptEditor(!showPromptEditor)}
                variant="outline"
                size="icon"
                className="p-1"
              >
                {showPromptEditor ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
            {showPromptEditor && (
              <Input
                type="text"
                value={aiPrompt}
                onChange={(e) => setAIPrompt(e.target.value)}
                placeholder="Adjust prompt for AI content generation"
              />
            )}
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="resize-none"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="image">Image</Label>
            <div className="flex flex-col gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={isGenerating}
                className="cursor-pointer"
              />

              {isGenerating && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Uploading...</span>
                </div>
              )}

              {uploadError && (
                <div className="text-red-500 text-sm">{uploadError}</div>
              )}

              {isImageLoading && !uploadError && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading image preview...</span>
                </div>
              )}

              {imageLoadError && !isImageLoading && (
                 <div className="flex items-center gap-2 text-red-500 text-sm p-2 border border-red-500 rounded-md bg-red-50 dark:bg-red-950/30">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>Error loading preview: {imageLoadError}</span>
                </div>
              )}

              {imageObjectUrl && !isImageLoading && !imageLoadError && (
                <div className="relative mt-2">
                  <img 
                    src={imageObjectUrl}
                    alt="Preview" 
                    className="w-full h-auto rounded-md border border-border object-cover max-h-[200px]" 
                    onError={() => {
                      console.error(`Failed to load image from object URL: ${imageObjectUrl}`);
                      setImageLoadError('Browser failed to display cached image.');
                      setImageObjectUrl(null);
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    aria-label="Remove image"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scheduled-time">Schedule Time</Label>
            
            {/* Quick Time Slot Selectors */}
            <div className="flex flex-wrap gap-2 mb-2">
              <Button 
                type="button" 
                size="sm"
                variant="outline"
                className="flex items-center gap-1 border-indigo-400 dark:border-indigo-500"
                onClick={() => handleTimeSlotSelect(optimalTimes[0])}
                title={`Morning (${optimalTimes[0].hour}:${optimalTimes[0].minute.toString().padStart(2, '0')})`}
              >
                <SunIcon className="h-4 w-4" />
                <span>Morning ({optimalTimes[0].hour}:{optimalTimes[0].minute.toString().padStart(2, '0')})</span>
              </Button>
              <Button 
                type="button" 
                size="sm"
                variant="outline"
                className="flex items-center gap-1 border-indigo-400 dark:border-indigo-500"
                onClick={() => handleTimeSlotSelect(optimalTimes[1])}
                title={`Noon (${optimalTimes[1].hour}:${optimalTimes[1].minute.toString().padStart(2, '0')})`}
              >
                <Clock3Icon className="h-4 w-4" />
                <span>Noon ({optimalTimes[1].hour}:{optimalTimes[1].minute.toString().padStart(2, '0')})</span>
              </Button>
              <Button 
                type="button" 
                size="sm"
                variant="outline"
                className="flex items-center gap-1 border-indigo-400 dark:border-indigo-500"
                onClick={() => handleTimeSlotSelect(optimalTimes[2])}
                title={`Evening (${optimalTimes[2].hour}:${optimalTimes[2].minute.toString().padStart(2, '0')})`}
              >
                <MoonIcon className="h-4 w-4" />
                <span>Evening ({optimalTimes[2].hour}:{optimalTimes[2].minute.toString().padStart(2, '0')})</span>
              </Button>
            </div>

            {/* Show which optimal slot was selected */}
            {!post && (
              <p className="text-xs text-muted-foreground mb-2">
                We've scheduled your post for the next available optimal time slot. Feel free to adjust.
              </p>
            )}
            
            <div className="relative">
              <DatePicker
                selected={scheduledTime}
                onChange={(date: Date | null) => date && setScheduledTime(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy h:mm aa"
                className="w-full rounded-md border-2 border-indigo-400 dark:border-indigo-500 
                  bg-background text-foreground px-3 py-2 text-sm shadow-sm
                  focus-visible:outline-none focus-visible:ring-2 
                  focus-visible:ring-indigo-400 dark:focus-visible:ring-indigo-500"
                wrapperClassName="w-full"
                popperClassName="react-datepicker-popper"
                calendarClassName="bg-card border-2 border-indigo-400 dark:border-indigo-500 text-foreground"
                dayClassName={() => "text-foreground hover:bg-indigo-100 dark:hover:bg-indigo-900"}
                timeClassName={() => "text-foreground"}
              />
              <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-2 border-indigo-400 dark:border-indigo-500"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createPost.isPending || updatePost.isPending || isGenerating}
              className="bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              {createPost.isPending || updatePost.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PostModal;